import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import LoadingSpinner from '../../components/LoadingSpinner';
import PaymentEventForm from './PaymentEventForm';
import PaymentsTable from './PaymentsTable';

// Las opciones dependen de la parada del micro, así que se guardan por evento.
// Estas son las habituales: se precargan al crear y se editan si hace falta.
const DEFAULT_COMMENT_OPTIONS = [
  { label: 'Ida y vuelta', requires_detail: false, sheet_color: null },
  { label: 'Solo vuelta', requires_detail: false, sheet_color: '#fce5cd' },
  { label: 'No sube en 3 y 48', requires_detail: true, sheet_color: '#f79902' },
];

const EMPTY_FORM = {
  title: '',
  amount: '',
  sheet_tab: '',
  bank_cbu: '',
  bank_alias: '',
  bank_holder: '',
  capacity: '',
  match_date: '',
  comment_options: DEFAULT_COMMENT_OPTIONS,
};

// El backend rechaza labels vacíos y espera null, no [], cuando no hay opciones.
const cleanCommentOptions = (options) => {
  const valid = (options || [])
    .filter((o) => o.label?.trim())
    .map((o) => ({
      label: o.label.trim(),
      requires_detail: !!o.requires_detail,
      sheet_color: o.sheet_color || null,
    }));
  return valid.length > 0 ? valid : null;
};

// El input datetime-local usa "YYYY-MM-DDTHH:mm" en hora local; el backend
// devuelve un ISO string. new Date() interpreta un ISO sin offset como hora
// local, así que el round-trip queda consistente sin manejar timezone.
const toDatetimeLocalValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const tabActiveStyle = {
  color: '#C73B33',
  fontWeight: 600,
  background: 'none',
  border: 'none',
  borderBottom: '2px solid #C73B33',
  padding: '10px 20px',
  cursor: 'pointer',
};

const tabInactiveStyle = {
  borderBottom: '2px solid transparent',
  color: '#666',
  background: 'none',
  border: 'none',
  padding: '10px 20px',
  cursor: 'pointer',
};

const Payments = () => {
  const [activeTab, setActiveTab] = useState('evento');
  const [payments, setPayments] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  // Event currently being edited (null = create mode).
  const [editingEvent, setEditingEvent] = useState(null);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(''), 4000);
  };

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/v1/payments');
      setPayments(response.data);
    } catch (err) {
      showError('Error al cargar los pagos.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchActiveEvent = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/payments/event/active');
      setActiveEvent(response.data || null);
    } catch (err) {
      setActiveEvent(null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEvents = useCallback(async () => {
    try {
      const response = await api.get('/api/v1/payments/events');
      setEvents(response.data || []);
    } catch (err) {
      showError('Error al cargar el historial de eventos.');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchPayments();
    fetchActiveEvent();
    fetchEvents();
  }, [fetchPayments, fetchActiveEvent, fetchEvents]);

  const handleInputChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const handleCommentOptionsChange = (options) => {
    setFormData((prev) => ({ ...prev, comment_options: options }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await api.post('/api/v1/payments/event', {
        title: formData.title,
        amount: parseFloat(formData.amount),
        sheet_tab: formData.sheet_tab || null,
        bank_cbu: formData.bank_cbu || null,
        bank_alias: formData.bank_alias || null,
        bank_holder: formData.bank_holder || null,
        capacity: formData.capacity ? parseInt(formData.capacity, 10) : null,
        match_date: formData.match_date || null,
        comment_options: cleanCommentOptions(formData.comment_options),
      });
      showSuccess('Evento creado correctamente.');
      setFormData({ ...EMPTY_FORM });
      fetchActiveEvent();
      fetchEvents();
    } catch (err) {
      showError('Error al crear el evento. Verificá los datos e intentá de nuevo.');
    }
  };

  const handleEditClick = (e) => {
    setEditingEvent(e);
    setFormData({
      title: e.title ?? '',
      amount: e.amount ?? '',
      sheet_tab: e.sheet_tab ?? '',
      bank_cbu: e.bank_cbu ?? '',
      bank_alias: e.bank_alias ?? '',
      bank_holder: e.bank_holder ?? '',
      capacity: e.capacity ?? '',
      match_date: toDatetimeLocalValue(e.match_date),
      comment_options: e.comment_options ?? [],
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingEvent(null);
    setFormData({ ...EMPTY_FORM });
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    try {
      await api.put(`/api/v1/payments/event/${editingEvent.id}`, {
        title: formData.title.trim(),
        amount: parseFloat(formData.amount),
        sheet_tab: formData.sheet_tab || null,
        bank_cbu: formData.bank_cbu || null,
        bank_alias: formData.bank_alias || null,
        bank_holder: formData.bank_holder || null,
        capacity: formData.capacity ? parseInt(formData.capacity, 10) : null,
        match_date: formData.match_date || null,
        comment_options: cleanCommentOptions(formData.comment_options),
      });
      showSuccess('Evento actualizado correctamente.');
      handleCancelEdit();
      fetchActiveEvent();
      fetchEvents();
      fetchPayments();
    } catch (err) {
      showError('Error al actualizar el evento. Verificá los datos e intentá de nuevo.');
    }
  };

  const handleToggleEvent = async (id) => {
    try {
      await api.put(`/api/v1/payments/event/${id}/toggle`);
      fetchEvents();
      fetchActiveEvent();
    } catch (err) {
      showError('Error al cambiar el estado del evento.');
    }
  };

  return (
    <div className="container" style={{ paddingTop: '24px' }}>

      {/* Feedback messages */}
      {error && (
        <div className="alert alert-danger" style={{ borderRadius: '6px' }}>{error}</div>
      )}
      {successMsg && (
        <div className="alert alert-success" style={{ borderRadius: '6px' }}>{successMsg}</div>
      )}

      {/* Internal tab bar */}
      <div
        style={{
          borderBottom: '1px solid #dee2e6',
          marginBottom: '28px',
          display: 'flex',
          gap: '4px',
        }}
      >
        <button
          style={activeTab === 'evento' ? tabActiveStyle : tabInactiveStyle}
          onClick={() => setActiveTab('evento')}
        >
          Evento Activo
        </button>
        <button
          style={activeTab === 'pagos' ? tabActiveStyle : tabInactiveStyle}
          onClick={() => setActiveTab('pagos')}
        >
          Pagos Recibidos
        </button>
      </div>

      {/* Tab 1 — Evento Activo */}
      {activeTab === 'evento' && (
        <div>
          {/* Active event banner */}
          <div
            style={{
              backgroundColor: activeEvent ? '#fff8f8' : '#f8f9fa',
              border: `1px solid ${activeEvent ? '#C73B33' : '#dee2e6'}`,
              borderRadius: '8px',
              padding: '16px 20px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <span style={{ fontSize: '1.4rem' }}>💳</span>
            <div>
              {activeEvent ? (
                <>
                  <strong style={{ color: '#C73B33' }}>Evento activo:</strong>{' '}
                  {activeEvent.title}
                  <span style={{ color: '#666', fontSize: '0.9rem' }}>
                    {' '}— Monto: ${Number(activeEvent.amount).toLocaleString('es-AR')}
                    {activeEvent.capacity != null && (
                      <> — Ocupados: {activeEvent.confirmed_count ?? 0}/{activeEvent.capacity}</>
                    )}
                  </span>
                </>
              ) : (
                <span style={{ color: '#888' }}>No hay ningún viaje activo.</span>
              )}
            </div>
          </div>

          {/* Create / edit event form */}
          <h5 style={{ marginBottom: '12px' }}>
            {editingEvent ? `Editar evento — ${editingEvent.title}` : 'Crear nuevo evento'}
          </h5>
          <PaymentEventForm
            formData={formData}
            onInputChange={handleInputChange}
            onCommentOptionsChange={handleCommentOptionsChange}
            onSubmit={editingEvent ? handleUpdate : handleSubmit}
            isEditing={!!editingEvent}
            onCancel={handleCancelEdit}
            confirmedCount={editingEvent?.confirmed_count ?? 0}
            originalTitle={editingEvent?.title ?? ''}
            originalAmount={editingEvent?.amount ?? ''}
          />

          {/* Events history table */}
          <div style={{ marginTop: '40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <h5 style={{ margin: 0 }}>Histórico de Eventos</h5>
              <button
                onClick={fetchEvents}
                className="btn btn-sm"
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  lineHeight: 1,
                  padding: '4px 8px',
                }}
                title="Actualizar eventos"
              >
                🔄
              </button>
            </div>

            {events.length === 0 ? (
              <p style={{ color: '#888', textAlign: 'center', marginTop: '20px' }}>
                No hay eventos registrados aún.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-hover table-bordered">
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th>Monto</th>
                      <th>Capacidad</th>
                      <th>Alias</th>
                      <th>Pestaña</th>
                      <th>Estado</th>
                      <th>Fecha de partido</th>
                      <th>Fecha de creación</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.id}>
                        <td>{e.title}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          ${Number(e.amount).toLocaleString('es-AR')}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.9rem' }}>
                          {e.capacity != null
                            ? `${e.confirmed_count ?? 0}/${e.capacity}`
                            : '—'}
                        </td>
                        <td style={{ color: '#666', fontSize: '0.9rem' }}>
                          {e.bank_alias || '—'}
                        </td>
                        <td style={{ color: '#666', fontSize: '0.9rem' }}>
                          {e.sheet_tab || '—'}
                        </td>
                        <td>
                          <span
                            style={{
                              backgroundColor: e.is_active ? '#d4edda' : '#e2e3e5',
                              color: e.is_active ? '#155724' : '#555',
                              padding: '3px 10px',
                              borderRadius: '12px',
                              fontSize: '0.82rem',
                              fontWeight: 600,
                              display: 'inline-block',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {e.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {e.match_date
                            ? new Date(e.match_date).toLocaleString('es-AR', {
                                day: '2-digit', month: '2-digit', year: 'numeric',
                                hour: '2-digit', minute: '2-digit',
                              })
                            : '—'}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {new Date(e.created_at).toLocaleDateString('es-AR')}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleEditClick(e)}
                              className="btn btn-sm"
                              style={{
                                backgroundColor: '#ffc107',
                                color: '#212529',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                padding: '4px 12px',
                                fontSize: '0.82rem',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => handleToggleEvent(e.id)}
                              className="btn btn-sm"
                              style={{
                                backgroundColor: e.is_active ? '#C73B33' : '#6c757d',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                padding: '4px 12px',
                                fontSize: '0.82rem',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {e.is_active ? 'Desactivar' : 'Activar'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab 2 — Pagos Recibidos */}
      {activeTab === 'pagos' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <h5 style={{ margin: 0 }}>Pagos Recibidos</h5>
            <button
              onClick={fetchPayments}
              className="btn btn-sm"
              style={{
                backgroundColor: 'transparent',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '1rem',
                lineHeight: 1,
                padding: '4px 8px',
              }}
              title="Actualizar pagos"
            >
              🔄
            </button>
          </div>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <PaymentsTable payments={payments} events={events} onRefresh={() => { fetchPayments(); fetchActiveEvent(); fetchEvents(); }} />
          )}
        </div>
      )}

    </div>
  );
};

export default Payments;
